import { Link } from 'react-router-dom';
import { line, BUDGET_TOO_LOW_ACTION_ID, Size, formatDateOnly, nextActionLevel, stalePillText, t, type NextActionBannerRow, type ProgressStageRow } from '@bms2/shared';
import { Pill } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { useDateFormat, useDocumentToday } from '../../shell/ViewerZone';
import styles from './DealPage.module.css';

/**
 * Deal Progress (piece 19; figures 81 and 83): the stage list drawn as a
 * checklist off the same gate data as the stages, never a second progress
 * field. Completed stages carry a filled green tick, the current stage a hollow
 * navy ring, remaining stages an empty ring in the page's own
 * edge colour, one vertical line joining them; the current stage's own
 * sub-stages hang beneath it. Every ring is one stroke weight and is centred
 * on both the connector and the first line of its label, and a row's pills sit
 * in a gutter at the right (product owner, 2026-09-14: the circles were ugly).
 * 🔨 At threshold the ring turns red and the STALE pill names the exact day
 * count, and no acknowledge-and-keep-waiting control exists.
 */
export interface DealProgressProps {
  rows: readonly ProgressStageRow[];
  stageLabel: string;
  boardRoute: string;
  escalates: boolean;
  /** The Next Action list the banner reads (decision 1485); the first row's level draws as a pill on the row that is the first action. */
  nextAction?: readonly NextActionBannerRow[];
}

export function DealProgress({ rows, stageLabel, boardRoute, escalates, nextAction = [] }: DealProgressProps) {
  const format = useDateFormat();
  const today = useDocumentToday();
  const level = nextActionLevel(nextAction, today);
  const firstId = nextAction[0]?.id ?? null;
  /** 🔒 The level's pill rides on the sub-stage row that is the first action; the budget talk is no sub-stage, so it rides on the current stage's row (decision 1485). */
  const budgetTalk = firstId === BUDGET_TOO_LOW_ACTION_ID;
  /** 🔒 Only an Urgent first action draws a pill; a Normal one tells nobody anything (decision 4810, amending 1485). */
  const levelPill = (on: boolean) => (on && level === 'urgent'
    ? (
      <span data-progress-level={level}>
        <Pill text={t('deal.progress.urgent')} foreground="var(--status-warning-text)" background="var(--status-warning-bg)" size={Size.Small} />
      </span>
    )
    : null);
  return (
    <div className={styles.progress} data-deal-progress={stageLabel}>
      <div className={styles.progressHead}>
        <Link to={boardRoute} className={styles.boardLink} data-deal-board-link="true">{t('deal.progress.board')}</Link>
      </div>
      <ol className={styles.ladder}>
        {rows.map((row) => (
          <li key={row.stage} className={styles.rung} data-progress-stage={row.stage} data-progress-state={row.state}>
            <span className={`${styles.ring} ${ringClass(row.state)}`} aria-hidden="true">
              {row.state === 'done' ? <Icon meaning="confirmed" size="inline" /> : null}
            </span>
            <span className={stageLabelClass(row.state)}>{row.stage}</span>
            {/* The pills ride in a gutter at the right, so every row's label starts and ends in one column (product owner, 2026-09-14). */}
            {row.state === 'now' || row.state === 'stale' ? (
              <span className={styles.pills}>
                <Pill text={t('deal.progress.now')} foreground="var(--navy-primary)" background="var(--selection-bg)" size={Size.Small} />
                {levelPill(budgetTalk)}
                {row.state === 'stale' ? <Pill text={stalePillText(row.days ?? 0)} foreground="var(--status-error-text)" background="var(--status-error-bg)" size={Size.Small} /> : null}
              </span>
            ) : null}
            {(row.state === 'now' || row.state === 'stale') && row.subStages.length > 0 ? (
              <ul className={styles.subLadder}>
                {row.subStages.map((sub) => (
                  <li key={sub.subStage} className={styles.subRung} data-progress-sub-stage={sub.subStage} data-progress-sub-state={sub.state}>
                    <span className={`${styles.ringSmall} ${ringClass(sub.state)}`} aria-hidden="true">
                      {sub.state === 'done' ? <Icon meaning="confirmed" size="inline" /> : null}
                    </span>
                    <span className={sub.state === 'na' ? styles.subLabelNa : sub.state === 'stale' ? styles.rungLabelStale : styles.subLabel}>{sub.state === 'na' ? line(sub.shortName, `· ${t('deal.progress.na')}`) : sub.shortName}</span>
                    {/* 🔒 An open sub-stage's Due reads beside it, where the Next Action tile once listed it (decision 1215); a row with none reads nothing. */}
                    {sub.dueDate && sub.applicable && !sub.completed ? <span className={styles.subDue} data-progress-sub-due={sub.subStage}>{line(t('deal.progress.due'), formatDateOnly(sub.dueDate, format))}</span> : null}
                    {sub.state === 'now' || sub.state === 'stale' || (!budgetTalk && firstId === sub.subStage) ? (
                      <span className={styles.pills}>
                        {sub.state === 'now' ? <Pill text={t('deal.progress.now')} foreground="var(--navy-primary)" background="var(--selection-bg)" size={Size.Small} /> : null}
                        {levelPill(!budgetTalk && firstId === sub.subStage)}
                        {sub.state === 'stale' ? <Pill text={stalePillText(sub.days ?? 0)} foreground="var(--status-error-text)" background="var(--status-error-bg)" size={Size.Small} /> : null}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
      {escalates ? <p className={styles.staleNote} data-deal-escalates="true">{t('deal.progress.escalates')}</p> : null}
    </div>
  );
}

/**
 * A stage row's label reads in one of four inks: the page's own for a stage
 * already passed, green for the stage the Deal is on, red where it is in
 * alarm, and muted for a stage not yet reached.
 */
function stageLabelClass(state: string): string {
  if (state === 'stale' || state === 'lost') return styles.rungLabelStale ?? '';
  if (state === 'now') return styles.rungLabelNow ?? '';
  if (state === 'done') return styles.rungLabelDone ?? '';
  return styles.rungLabel ?? '';
}

/**
 * The condensed indicator (figure 82): one lettered ring per stage, pinned at
 * the right when the panel is collapsed and across the top on a phone. The
 * same states as the ladder, read off the same rows.
 */
/**
 * The ring's state class. 🔨 Looked up by name, never by template: the build
 * camel-cases module class names, so `styles['ring_done']` is undefined and a
 * done stage drew hollow (product owner, 2026-09-12).
 */
export function ringClass(state: string): string {
  const classes: Record<string, string | undefined> = { done: styles.ringDone, now: styles.ringNow, stale: styles.ringStale, na: styles.ringNa, lost: styles.ringLost };
  return classes[state] ?? '';
}

export function ProgressRail({ rows }: { rows: readonly ProgressStageRow[] }) {
  return (
    <ol className={styles.rail} aria-label={t('deal.progress.panel')}>
      {rows.map((row) => (
        <li key={row.stage} className={styles.railStep} data-rail-stage={row.stage} data-rail-state={row.state} title={row.stage}>
          <span className={`${styles.railRing} ${ringClass(row.state)}`}>{row.state === 'done' ? <Icon meaning="confirmed" size="inline" /> : row.stage.charAt(0)}</span>
        </li>
      ))}
    </ol>
  );
}
